import React from 'react'
import PropTypes from 'prop-types'
import { View } from '@olxbr/design-system'
import styled, { createGlobalStyle } from 'styled-components'
import { BottomSheet as BottomSpringSheet } from 'react-spring-bottom-sheet'
import Svg from '../../atoms/Svg'

const leftArrowSvgPath =
  'M5.81 12.75l4.72 4.72a.75.75 0 0 1-1.06 1.06l-6-6a.75.75 0 0 1 0-1.06l6-6a.75.75 0 0 1 1.06 1.06l-4.72 4.72H20a.75.75 0 1 1 0 1.5H5.81z'

const closeSvgPath =
  'M13.06 12l5.47 5.47a.75.75 0 0 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 1.06-1.06L12 10.94l5.47-5.47a.7    5.75 0 0 1 1.06 1.06L13.06 12z'

const TitleBar = styled(View)`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  height: 32px;
`

const LeftArea = styled.button`
  font-size: 18px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
`

const RightArea = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: flex-end;
  background: transparent;
`

const Close = styled.a`
  &:hover {
    cursor: pointer;
  }
`

export const HeaderWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  height: 32px;
`

export const Body = styled.div`
  padding: 16px;
`

export const BottomSheetContainer = styled(BottomSpringSheet)`
  & [data-rsbs-header] {
    box-shadow: none !important;
    padding-bottom: 0;
  }

  & [data-rsbs-overlay] {
    height: auto !important;
    max-height: calc(100% - 24px);
  }

  & [data-rsbs-header]:before {
    background-color: transparent !important;
  }
`

const BottomSheet = props => {
  const {
    open,
    onClose,
    onLeftClick,
    children,
    footer,
    bodyStyle,
    showCloseButton,
    showBackButton,
  } = props

  return (
    <React.Fragment>
      <GlobalStyles />
      <BottomSheetContainer
        open={open}
        footer={footer}
        onDismiss={onClose}
        snapPoints={({ maxHeight }) => maxHeight - 32}
        data-testid="bottom-sheet"
      >
        <Body style={bodyStyle}>
          <TitleBar>
            {showBackButton && (
              <LeftArea href={null} onClick={onLeftClick}>
                <Svg
                  svgPath={leftArrowSvgPath}
                  height={24}
                  width={24}
                  color="#4A4A4A"
                  viewBox="0 0 24 24"
                />
              </LeftArea>
            )}
            {showCloseButton && (
              <RightArea>
                <Close href={null} onClick={onClose}>
                  <Svg
                    svgPath={closeSvgPath}
                    height={24}
                    width={24}
                    color="#4A4A4A"
                    viewBox="0 0 24 24"
                  />
                </Close>
              </RightArea>
            )}
          </TitleBar>
          {children}
        </Body>
      </BottomSheetContainer>
    </React.Fragment>
  )
}

BottomSheet.defaultProps = {
  showBackButton: false,
  showCloseButton: true,
}

BottomSheet.propTypes = {
  bodyStyle: PropTypes.objectOf({}),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onLeftClick: PropTypes.func,
  showBackButton: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  footer: PropTypes.elementType,
}

export default BottomSheet

const GlobalStyles = createGlobalStyle`
  [data-rsbs-overlay] {
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    display: flex;
    background: #fff;
    background: var(--rsbs-bg, #fff);
    flex-direction: column;
    height: 0px;
    height: var(--rsbs-overlay-h, 0px);
    transform: translate3d(0, 0px, 0);
    transform: translate3d(0, var(--rsbs-overlay-translate-y, 0px), 0);
    will-change: height;
  }

  [data-rsbs-overlay]:focus {
    outline: none;
  }

  [data-rsbs-is-blocking='false'] [data-rsbs-overlay] {
    box-shadow: 0 -5px 60px 0 rgba(38, 89, 115, 0.11), 0 -1px 0 rgba(38, 89, 115, 0.05);
  }

  [data-rsbs-overlay],
  [data-rsbs-root]:after {
    max-width: auto;
    max-width: var(--rsbs-max-w, auto);
    margin-left: env(safe-area-inset-left);
    margin-left: var(--rsbs-ml, env(safe-area-inset-left));
    margin-right: env(safe-area-inset-right);
    margin-right: var(--rsbs-mr, env(safe-area-inset-right));
  }

  [data-rsbs-overlay],
  [data-rsbs-backdrop],
  [data-rsbs-root]:after {
    z-index: 3;
    -ms-scroll-chaining: none;
    overscroll-behavior: none;
    touch-action: none;
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
  }

  [data-rsbs-backdrop] {
    top: -60px;
    bottom: -60px;
    background-color: rgba(0, 0, 0, 0.6);
    background-color: var(--rsbs-backdrop-bg, rgba(0, 0, 0, 0.6));
    will-change: opacity;
    cursor: pointer;
    opacity: 1;
  }

  [data-rsbs-is-dismissable='false'] [data-rsbs-backdrop] {
    cursor: ns-resize;
  }

  [data-rsbs-root]:after {
    content: '';
    pointer-events: none;
    background: #fff;
    background: var(--rsbs-bg, #fff);
    height: 1px;
    transform-origin: bottom;
    transform: scale3d(1, 0, 1);
    transform: scale3d(1, var(--rsbs-antigap-scale-y, 0), 1);
    will-change: transform;
  }

  [data-rsbs-footer],
  [data-rsbs-header] {
    flex-shrink: 0;
    cursor: ns-resize;
    padding: 16px;
  }

  [data-rsbs-header] {
    text-align: center;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    box-shadow: 0 1px 0 rgba(46, 59, 66, calc(1 * 0.125));
    box-shadow: 0 1px 0 rgba(46, 59, 66, calc(var(--rsbs-content-opacity, 1) * 0.125));
    z-index: 1;
    padding-top: calc(20px + env(safe-area-inset-top));
    padding-bottom: 8px;
  }

  [data-rsbs-header]:before {
    position: absolute;
    content: '';
    display: block;
    width: 36px;
    height: 4px;
    top: calc(8px + env(safe-area-inset-top));
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
    background-color: hsla(0, 0%, 0%, 0.14);
    background-color: var(--rsbs-handle-bg, hsla(0, 0%, 0%, 0.14));
  }

  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 2dppx) {
    [data-rsbs-header]:before {
      transform: translateX(-50%) scaleY(0.75);
    }
  }

  [data-rsbs-has-header='false'] [data-rsbs-header] {
    box-shadow: none;
    padding-top: calc(12px + env(safe-area-inset-top));
  }

  [data-rsbs-scroll] {
    flex-shrink: 1;
    flex-grow: 1;
    -webkit-tap-highlight-color: revert;
    -webkit-touch-callout: revert;
    -webkit-user-select: auto;
    -ms-user-select: auto;
    -moz-user-select: auto;
    user-select: auto;
    overflow: auto;
    -ms-scroll-chaining: none;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  [data-rsbs-scroll]:focus {
    outline: none;
  }

  [data-rsbs-has-footer='false'] [data-rsbs-content] {
    padding-bottom: env(safe-area-inset-bottom);
  }

  [data-rsbs-content] {
    overflow: hidden;
  }

  [data-rsbs-footer] {
    box-shadow: 0 -1px 0 rgba(46, 59, 66, calc(1 * 0.125)), 0 2px 0 #fff;
    box-shadow: 0 -1px 0 rgba(46, 59, 66, calc(var(--rsbs-content-opacity, 1) * 0.125)), 0 2px 0 var(--rsbs-bg, #fff);
    overflow: hidden;
    z-index: 1;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
  }

  [data-rsbs-is-dismissable='true'] [data-rsbs-header] > *,
  [data-rsbs-is-dismissable='true'] [data-rsbs-scroll] > *,
  [data-rsbs-is-dismissable='true'] [data-rsbs-footer] > *,
  [data-rsbs-is-dismissable='false'][data-rsbs-state='opening'] [data-rsbs-header] > *,
  [data-rsbs-is-dismissable='false'][data-rsbs-state='closing'] [data-rsbs-header] > *,
  [data-rsbs-is-dismissable='false'][data-rsbs-state='opening'] [data-rsbs-scroll] > *,
  [data-rsbs-is-dismissable='false'][data-rsbs-state='closing'] [data-rsbs-scroll] > *,
  [data-rsbs-is-dismissable='false'][data-rsbs-state='opening'] [data-rsbs-footer] > *,
  [data-rsbs-is-dismissable='false'][data-rsbs-state='closing'] [data-rsbs-footer] > * {
    opacity: 1;
    opacity: var(--rsbs-content-opacity, 1);
  }

  [data-rsbs-is-dismissable='true'] [data-rsbs-backdrop],
  [data-rsbs-is-dismissable='false'][data-rsbs-state='opening'] [data-rsbs-backdrop],
  [data-rsbs-is-dismissable='false'][data-rsbs-state='closing'] [data-rsbs-backdrop] {
    opacity: 1;
    opacity: var(--rsbs-backdrop-opacity, 1);
  }

  [data-rsbs-state='closed'],
  [data-rsbs-state='closing'] {
    pointer-events: none;
  }
`
